import useSWR from "swr";
import { useAuth } from "./useAuth";
import { debounce } from "../util";
import log from "@app/util/log";

const parse = async (res, expect, count) => {
	let body;
	switch (expect) {
		case "none":
			body = null;
			break;
		case "csv":
		case "text":
			body = await res.text();
			break;
		case "blob":
			body = await res.blob();
			break;
		case "json":
		default: {
			body = await res.json();
			if (count) {
				const cr = res.headers.get("content-range");
				if (cr) {
					const [_, total] = cr.split("/");
					body._count = parseInt(total);
				}
			}
		}
	}
	return body;
};

export const AuthCode = {
	BANNED: "BANNED",
	UNREGISTERED: "UNREGISTERED",
	REQUIRES_SYNC: "REQUIRES SYNC",
	AUTHENTICATION_FAILED: "AUTHENTICATION FAILED",
} as const;

const STATUS_TITLES = {
	400: "Bad Request",
	401: "Unauthorized",
	403: "Forbidden",
	426: "Sync required",
	501: "Not Implemented",
};

const syncAccessPromises = new Map<string, Promise<void>>();

const SYNC_RELOADED_KEY = "portal:access-sync-reloaded";

const tokenSubject = (token: string) => {
	try {
		const payload = token
			.split(".")[1]
			.replace(/-/g, "+")
			.replace(/_/g, "/");
		return JSON.parse(atob(payload)).sub ?? "unknown";
	} catch {
		return "unknown";
	}
};

const hasSyncReloaded = (token: string) => {
	try {
		return (
			sessionStorage.getItem(SYNC_RELOADED_KEY) === tokenSubject(token)
		);
	} catch {
		return false;
	}
};

const markSyncReloaded = (token: string) => {
	try {
		sessionStorage.setItem(SYNC_RELOADED_KEY, tokenSubject(token));
	} catch {}
};

const clearSyncReloaded = (token: string) => {
	try {
		if (hasSyncReloaded(token))
			sessionStorage.removeItem(SYNC_RELOADED_KEY);
	} catch {}
};

const syncAccess = (login, logout, token) => {
	const subject = tokenSubject(token);
	const inFlight = syncAccessPromises.get(subject);
	if (inFlight) {
		return inFlight;
	}
	const promise = fetcher(
		login,
		logout,
		token
	)({
		url: "rpc/access",
		method: "POST",
		expect: "none",
		schema: "portal",
		isSyncRequest: true,
		logError: false,
	})
		.then(
			() => {
				markSyncReloaded(token);
				location.reload();
			},
			(error) => {
				log.error(error, { url: "rpc/access", method: "POST" });
				throw error;
			}
		)
		.finally(() => {
			if (syncAccessPromises.get(subject) === promise) {
				syncAccessPromises.delete(subject);
			}
		});
	syncAccessPromises.set(subject, promise);
	return promise;
};

const signals = {};

const getUrl = (url, opts) => {
	return (
		opts.signalKey ??
		`${opts.method ?? "GET"}:${opts.schema ?? "public"}.${url}`
	);
};

const authCode = (message?: string) =>
	Object.values(AuthCode).find((code) => message?.includes(code));

const fetcher =
	(login: () => void, logout: () => void, token: string) =>
	(
		opts: UseApiOptions & {
			url: string;
			query?: Record<string, any>;
			params?: Record<string, any>;
		}
	) => {
		if (!opts || !opts.url) {
			throw new Error("Skipping request");
		}
		const {
			url,
			query = {},
			params,
			method = "GET",
			expect = "json",
			one = false,
			count = false,
			schema = "public",
			contenttype,
			errorhandler,
			skipError = false,
			logError = true,
			soft = false,
			signal,
			signalKey,
			isSyncRequest = false,
		} = opts;

		if (!token) {
			return Promise.reject(new Error("Unauthenticated"));
		}

		const request = {
			method,
			headers: {
				Authorization: token ? `Bearer ${token}` : undefined,
				"Content-Type": contenttype || "application/json",
			},
			body: params ? JSON.stringify(params) : undefined,
			signal:
				signal instanceof AbortController
					? signal.signal
					: signal instanceof AbortSignal
						? signal
						: undefined,
		};
		if (one)
			request.headers["Accept"] = "application/vnd.pgrst.object+json";
		if (expect === "csv") request.headers["Accept"] = "text/csv";
		if (count) request.headers["Prefer"] = "count=exact";

		if (schema) {
			switch (method) {
				case "GET":
				case "HEAD": {
					request.headers["Accept-Profile"] = schema;
					break;
				}

				case "POST":
					// copied from pgrest.js
					if (
						request.headers["Content-Type"] ===
						"multipart/form-data"
					) {
						console.warn(
							"Removing content-type for multipart/form-data requests"
						);
						delete request.headers["Content-Type"]; // OMFG...
					}
				// fall through here
				// eslint-disable-next-line no-fallthrough
				case "PATCH":
				case "DELETE": {
					request.headers["Prefer"] = "return=representation";
					request.headers["Content-Profile"] = schema;

					break;
				}

				default:
					break;
			}
		}

		return fetch(
			(url.startsWith("http") ? "" : API_URL) +
				(url.startsWith("/") || url.startsWith("http") ? "" : "/") +
				url +
				"?" +
				new URLSearchParams(query).toString(),
			request
		)
			.then(async (res) => {
				let title, message;
				let type = "error";
				if (method === "HEAD") {
					return res.headers;
				} else if (
					res.headers.get("Content-Length") === "0" &&
					expect !== "none"
				) {
					title = "No response";
					message = "Empty response from server";
					errorhandler?.({ type, title, message });
					if (skipError) {
						console.warn("Skipping error", {
							type,
							title,
							message,
							url,
							method,
						});
						return null;
					}
					throw new Error(message);
				} else if (res.ok) {
					if (!isSyncRequest) clearSyncReloaded(token);
					const result = await parse(res, expect, count);
					return result;
				} else if (!res.ok) {
					let code;
					let logged = false;
					switch (res.status) {
						case 0: {
							title = "Connection error";
							message = "No response from server";
							break;
						}

						case 400:
						case 401:
						case 403:
						case 426:
						case 501: {
							const body = await res
								.json()
								.catch((parseError) => {
									log.error(parseError, {
										url,
										method,
										status: res.status,
									});
									return {};
								});
							title =
								STATUS_TITLES[res.status] ||
								`${res.status}: ${res.statusText}`;
							message = body.message;
							code = authCode(message);

							if (code === AuthCode.BANNED) {
								message = AuthCode.BANNED;
							} else if (
								(code === AuthCode.UNREGISTERED ||
									code === AuthCode.REQUIRES_SYNC) &&
								!isSyncRequest &&
								!hasSyncReloaded(token)
							) {
								try {
									await syncAccess(login, logout, token);
									return new Promise(() => {});
								} catch {
									// fall through
								}
							} else if (
								code === AuthCode.AUTHENTICATION_FAILED
							) {
								if (res.status === 501) {
									log.error(
										new Error(
											"API bug: AUTHENTICATION FAILED returned with status 501",
											{
												cause: {
													status: res.status,
													code,
												},
											}
										),
										{ url, method, status: res.status }
									);
									logged = true;
								} else {
									log.error(
										new Error(
											"AUTHENTICATION FAILED - logging out"
										),
										{
											url,
											method,
											status: res.status,
											code,
										}
									);
									logged = true;
									logout();
								}
							} else if (res.status === 401) {
								if (
									message?.match(
										/JWT expired|LOGGED OUT|Unauthorized/
									)
								) {
									if (
										token &&
										token ===
											request.headers[
												"Authorization"
											]?.replace("Bearer ", "")
									) {
										log.error(
											new Error(
												"JWT expired - re-authenticating"
											),
											{
												url,
												method,
												status: res.status,
												message,
											}
										);
										logged = true;
										login();
									} else {
										throw new Error("Unauthenticated");
									}
								} else if (message?.match(/JWSError .*/)) {
									log.error(
										new Error(
											"Invalid authentication token - logging out"
										),
										{
											url,
											method,
											status: res.status,
											message,
										}
									);
									message = "Invalid authentication token.";

									logout();
									return;
								}
							}

							break;
						}

						case 406: {
							title = "Nothing returned";
							message =
								"Requested object does not exist OR not enough permissions.";

							if (soft) {
								console.warn("SOFT request", {
									url,
									method,
									query,
								});
								throw new Error("SOFT request");
							}

							break;
						}

						case 500: {
							title = "Server error";
							message = "Unexpected server error.";
							break;
						}

						case 502: {
							title = "Service is not running!";
							message = `Bad gateway.`;
							break;
						}

						default: {
							const body = await res.json();
							title = `${res.status}: ${res.statusText}`;
							message = body.message;

							break;
						}
					}
					errorhandler?.({ type, title, message });

					if (skipError) {
						console.warn("Skipping error", {
							type,
							title,
							message,
							url,
							method,
						});
						return null;
					}

					// toast.error(title + (message ? ": " + message : ""));
					const err = new Error(message || title, {
						cause: { status: res.status, code },
					});
					if (logError && !logged) {
						log.error(err, { url, method, status: res.status });
					}
					throw err;
				}
			})
			.catch((err) => {
				if (err?.name === "AbortError") {
					const urlKey = getUrl(url, { method, schema, signalKey });
					// console.log("Request aborted for", urlKey);
					return;
				}
				throw err;
			})
			.finally(() => {
				const urlKey = getUrl(url, { method, schema, signalKey });
				// Clear the signal after the request completes
				if (signals[urlKey] === signal) {
					// console.log("Clearing signal for", urlKey);
					delete signals[urlKey];
				}
			});
	};

type UseApiOptions = {
	signal?: boolean | AbortController | AbortSignal;
	signalKey?: string;
	method?: string;
	expect?: "json" | "text" | "csv" | "blob" | "none";
	one?: boolean;
	skipError?: boolean;
	logError?: boolean;
	errorhandler?: (error: {
		type: string;
		title: string;
		message: string;
	}) => void;
	soft?: boolean;
	count?: boolean;
	schema?: string;
	isSyncRequest?: boolean;
	contenttype?: string;
	refreshInterval?: number;
	revalidateOnFocus?: boolean;
	// revalidateOnMount?: boolean;
	revalidateOnReconnect?: boolean;
	refreshWhenOffline?: boolean;
	refreshWhenHidden?: boolean;
	shouldRetryOnError?: boolean;
	errorRetryCount?: number;
	errorRetryInterval?: number;
	[key: string]: any; // for any additional options
};

const useApi = (
	url,
	params,
	{
		signal = true,
		method = "GET",
		expect = "json",
		one = false,
		skipError = false,
		logError = true,
		errorhandler = undefined,
		soft = false,
		count = false,
		schema = "public",
		signalKey = undefined,
		contenttype = undefined,
		refreshInterval = 0,
		revalidateOnFocus = false,
		// revalidateOnMount = false,
		revalidateOnReconnect = false,
		refreshWhenOffline = false,
		refreshWhenHidden = false,
		shouldRetryOnError = false,
		errorRetryCount = 3,
		errorRetryInterval = 5000,
		...opts
	}: UseApiOptions = {}
) => {
	const { login, logout, token } = useAuth();
	const ftchr = fetcher(login, logout, token);
	const { payload, ...query } = params || {};
	const swrKey = !url
		? null
		: {
				url,
				query,
				params: payload,
				method,
				expect,
				one,
				skipError,
				logError,
				count,
				schema,
				signalKey,
				contenttype,
				soft,
			};
	const urlKey = getUrl(url, { method, schema, signalKey });

	return useSWR(
		swrKey,
		(key) => {
			let ac: AbortController | undefined;
			if (signal === true) {
				// console.log("Creating new AbortController for", urlKey);
				ac = new AbortController();
				const old = signals[urlKey];
				if (old && !old.signal.aborted) {
					// console.log("Aborting previous request for", urlKey);
					old.abort();
				}
				signals[urlKey] = ac;
			}
			return ftchr({ ...key, errorhandler, signal: ac ?? signal });
		},
		{
			revalidateOnFocus,
			// revalidateOnMount,
			revalidateOnReconnect,
			refreshWhenOffline,
			refreshWhenHidden,
			refreshInterval,
			shouldRetryOnError,
			errorRetryCount,
			errorRetryInterval,
			...opts,
		}
	);
};

export const callAPI = async (
	url,
	{
		signal,
		query = {},
		params = undefined,
		payload = undefined,
		method = "GET",
		expect = "json",
		one = false,
		count = false,
		schema = "public",
		contenttype,
		errorhandler,
		skipError = false,
		logError = true,
		soft = false,
		login = undefined,
		logout = undefined,
		...opts
	}: UseApiOptions = {},
	token: string
) => {
	const ftchr = fetcher(
		login || window.login_fn,
		logout || window.logout_fn,
		token
	);

	return ftchr({
		signal:
			signal instanceof AbortController || signal instanceof AbortSignal
				? signal
				: undefined,
		url,
		method,
		query,
		params: params || payload,
		expect,
		one,
		count,
		schema,
		contenttype,
		errorhandler,
		skipError,
		logError,
		soft,
		...opts,
	});
};

export const debouncedCallAPI = debounce(
	async (table, options, callback, errorCallback, token) => {
		try {
			const result = await callAPI(table, options, token);
			callback?.(result);
			return result;
		} catch (error) {
			errorCallback?.(error);
		}
		return null;
	},
	500
);

export const useCallAPI = () => {
	const { login, logout, token } = useAuth();
	return async (url, options) => {
		return callAPI(url, { ...options, login, logout }, token);
	};
};
export const useDebouncedCallAPI = () => {
	const { login, logout, token } = useAuth();
	return async (table, options, callback, errorCallback) => {
		return debouncedCallAPI(
			table,
			{ ...options, login, logout },
			callback,
			errorCallback,
			token
		);
	};
};
export default useApi;
